import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  PushpinFilled,
  PushpinOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Input, message, Tooltip } from 'antd';
import React, { useRef, useState } from 'react';
import { useGetMediaFolders } from '../../../../utils/api/medias/useGetMediaFolders';
import { useResolveMediaPath } from '../../../../utils/api/medias/useResolveMediaPath';
import { useProfile } from '../../../../utils/zustand/hooks/useProfile';
import AddressBar, { ROOT_LABEL } from './AddressBar';
import MediaFilesTable from './MediaFilesTable';
import MediaPreviewModal from './MediaPreviewModal';
import QuickAccessPane from './QuickAccessPane';
import {
  getResolvePathErrorMessage,
  joinMediaPath,
  looksLikeMediaPath,
} from './mediaPath';
import { QUICK_ACCESS_MAX, useQuickAccess } from './useQuickAccess';

const getParentPath = (path) => path.split('/').slice(0, -1).join('/');

// Le mode d'affichage choisi est retenu d'une visite à l'autre.
const VIEW_MODE_KEY = 'promethee.mediaLibrary.viewMode';

const readViewMode = () => {
  try {
    return window.localStorage.getItem(VIEW_MODE_KEY) === 'icons'
      ? 'icons'
      : 'details';
  } catch {
    return 'details';
  }
};

const INITIAL_FILE_PARAMS = {
  search: '',
  order_by: 'created_at',
  media_type: '',
  already_sent_for_qualification: null,
  page: 1,
  pageSize: 10,
};

// Bibliothèque média façon explorateur Windows : on entre dans un dossier par
// double-clic, on navigue avec précédent / suivant / parent et la barre
// d'adresse, dans laquelle on peut coller un chemin O:\Gamme\Produit.
const MediaLibrary = () => {
  const [currentPath, setCurrentPath] = useState('');
  const [history, setHistory] = useState({ back: [], forward: [] });
  const [focusedKey, setFocusedKey] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [fileParams, setFileParams] = useState(INITIAL_FILE_PARAMS);
  const [previewFile, setPreviewFile] = useState(null);
  const [viewMode, setViewMode] = useState(readViewMode);
  const { profile } = useProfile();
  const quickAccess = useQuickAccess(profile?.sub ?? profile?.email);
  const { resolveMediaPath, isLoading: isResolvingPath } =
    useResolveMediaPath();
  // Seule la dernière soumission de chemin peut changer de dossier.
  const pathSubmissionRef = useRef(0);

  const { folders, files, pagination, error, isLoading } = useGetMediaFolders({
    path: currentPath,
    ...fileParams,
  });

  const showView = (path, { fileName, focus } = {}) => {
    // Toute navigation annule une résolution de chemin encore en cours.
    pathSubmissionRef.current += 1;
    setCurrentPath(path);
    setFocusedKey(focus ?? (fileName ? joinMediaPath(path, fileName) : null));
    setSelectedRowKeys(fileName ? [joinMediaPath(path, fileName)] : []);
    // Comme sous Windows, changer de dossier efface la recherche.
    setFileParams((prev) => ({ ...prev, page: 1, search: fileName || '' }));
  };

  const navigate = (path, options) => {
    if (path !== currentPath) {
      setHistory((prev) => ({
        back: [...prev.back, currentPath],
        forward: [],
      }));
    }
    showView(path, options);
  };

  const goBack = () => {
    const previous = history.back[history.back.length - 1];
    setHistory({
      back: history.back.slice(0, -1),
      forward: [currentPath, ...history.forward],
    });
    showView(previous, { focus: currentPath });
  };

  const goForward = () => {
    const [next, ...forward] = history.forward;
    setHistory({ back: [...history.back, currentPath], forward });
    showView(next);
  };

  const goUp = () =>
    navigate(getParentPath(currentPath), { focus: currentPath });

  // Renvoie true si le chemin a été ouvert.
  const handlePathSubmit = async (rawPath) => {
    pathSubmissionRef.current += 1;
    const submission = pathSubmissionRef.current;
    try {
      const { path, file_name: fileName } = await resolveMediaPath(rawPath);
      if (submission !== pathSubmissionRef.current) return false;
      navigate(path, { fileName });
      return true;
    } catch (resolveError) {
      if (submission === pathSubmissionRef.current) {
        message.error(getResolvePathErrorMessage(resolveError, rawPath));
      }
      return false;
    }
  };

  const handleRowClick = (record, event) => {
    setFocusedKey(record.key);
    if (record.isFolder) {
      // Comme sous Windows : sélectionner un dossier vide la sélection.
      setSelectedRowKeys([]);
    } else if (event.ctrlKey || event.metaKey) {
      setSelectedRowKeys((prev) =>
        prev.includes(record.key)
          ? prev.filter((key) => key !== record.key)
          : [...prev, record.key]
      );
    } else {
      setSelectedRowKeys([record.key]);
    }
  };

  // Double-clic : un dossier s'ouvre, un fichier s'affiche en aperçu.
  const handleRowOpen = (record) => {
    if (record.isFolder) navigate(record.path);
    else setPreviewFile(record);
  };

  const togglePin = (path) =>
    quickAccess.isPinned(path)
      ? quickAccess.unpin(path)
      : quickAccess.pin(path);

  // Clic droit sur un dossier : ouvrir, épingler ou retirer de l'accès rapide.
  const getRowMenuItems = (record) =>
    record.isFolder
      ? [
          { key: 'open', label: 'Ouvrir' },
          quickAccess.isPinned(record.path)
            ? {
                key: 'unpin',
                label: "Retirer de l'accès rapide",
                icon: <PushpinFilled />,
              }
            : {
                key: 'pin',
                label: "Épingler à l'accès rapide",
                icon: <PushpinOutlined />,
                disabled: quickAccess.isFull,
              },
        ]
      : null;

  const handleRowMenuClick = (key, record) => {
    if (key === 'open') navigate(record.path);
    if (key === 'pin') quickAccess.pin(record.path);
    if (key === 'unpin') quickAccess.unpin(record.path);
  };

  const isCurrentPinned = quickAccess.isPinned(currentPath);
  let pinTitle = isCurrentPinned
    ? "Retirer ce dossier de l'accès rapide"
    : "Épingler ce dossier à l'accès rapide";
  if (!currentPath) pinTitle = 'Ouvrez un dossier pour pouvoir l’épingler';
  else if (!isCurrentPinned && quickAccess.isFull) {
    pinTitle = `Accès rapide plein (${QUICK_ACCESS_MAX} raccourcis au maximum)`;
  }

  const handleSearchPaste = (event) => {
    // Un chemin collé dans la recherche ouvre le dossier, comme la barre d'adresse.
    const pasted = event.clipboardData.getData('text');
    if (!looksLikeMediaPath(pasted)) return;
    event.preventDefault();
    handlePathSubmit(pasted);
  };

  const setFileParam = (key) => (value) =>
    setFileParams((prev) => ({ ...prev, [key]: value, page: 1 }));

  const handleTableChange = (_pagination, _filters, sorter) => {
    setFileParams((prev) => ({
      ...prev,
      page: 1,
      order_by:
        sorter.field && sorter.order
          ? `${sorter.order === 'descend' ? '-' : ''}${sorter.field}`
          : 'created_at',
    }));
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // Stockage indisponible : le choix vaut pour la session.
    }
  };

  const handlePageChange = (page, pageSize) => {
    setFileParams((prev) => ({ ...prev, page, pageSize }));
  };

  // Les dossiers ne sont pas paginés par le back : ils ouvrent la première page.
  const rows = [
    ...(fileParams.page === 1
      ? folders.map((folder) => ({
          ...folder,
          key: folder.path,
          isFolder: true,
        }))
      : []),
    ...files.map((file) => ({ ...file, isFolder: false })),
  ];
  const currentName = currentPath ? currentPath.split('/').pop() : ROOT_LABEL;

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '8px 8px 0',
        }}
      >
        <Tooltip title="Précédent">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Précédent"
            disabled={history.back.length === 0}
            onClick={goBack}
          />
        </Tooltip>
        <Tooltip title="Suivant">
          <Button
            type="text"
            icon={<ArrowRightOutlined />}
            aria-label="Suivant"
            disabled={history.forward.length === 0}
            onClick={goForward}
          />
        </Tooltip>
        <Tooltip title="Dossier parent">
          <Button
            type="text"
            icon={<ArrowUpOutlined />}
            aria-label="Dossier parent"
            disabled={!currentPath}
            onClick={goUp}
          />
        </Tooltip>
        <Tooltip title={pinTitle}>
          <Button
            type="text"
            icon={
              isCurrentPinned ? (
                <PushpinFilled aria-hidden />
              ) : (
                <PushpinOutlined aria-hidden />
              )
            }
            aria-label={pinTitle}
            aria-pressed={isCurrentPinned}
            disabled={!currentPath || (!isCurrentPinned && quickAccess.isFull)}
            onClick={() => togglePin(currentPath)}
          />
        </Tooltip>
        <AddressBar
          path={currentPath}
          onNavigate={(path) => navigate(path)}
          onPathSubmit={handlePathSubmit}
          isResolving={isResolvingPath}
        />
        <Input
          aria-label="Rechercher dans le dossier"
          placeholder={`Rechercher dans ${currentName}`}
          prefix={<SearchOutlined />}
          value={fileParams.search}
          onChange={(event) => setFileParam('search')(event.target.value)}
          onPaste={handleSearchPaste}
          allowClear
          style={{ width: 280, marginLeft: 4 }}
        />
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <QuickAccessPane
          shortcuts={quickAccess.shortcuts}
          currentPath={currentPath}
          onNavigate={(path) => navigate(path)}
          onUnpin={quickAccess.unpin}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <MediaFilesTable
            dataSource={rows}
            folderCount={folders.length}
            selectedRowKeys={selectedRowKeys}
            onSelectionChange={setSelectedRowKeys}
            focusedKey={focusedKey}
            onRowClick={handleRowClick}
            onRowOpen={handleRowOpen}
            pagination={pagination}
            onPageChange={handlePageChange}
            onTableChange={handleTableChange}
            filterBy={fileParams.media_type}
            onFilterChange={setFileParam('media_type')}
            qualificationFilterBy={fileParams.already_sent_for_qualification}
            onQualificationFilterChange={setFileParam(
              'already_sent_for_qualification'
            )}
            isLoading={isLoading}
            notFound={error?.response?.status === 404}
            isSearching={Boolean(fileParams.search)}
            getRowMenuItems={getRowMenuItems}
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            sortValue={fileParams.order_by}
            onSortChange={setFileParam('order_by')}
            onRowMenuClick={handleRowMenuClick}
            notFoundAction={
              isCurrentPinned && (
                <Button onClick={() => quickAccess.unpin(currentPath)}>
                  Retirer de l’accès rapide
                </Button>
              )
            }
          />
        </div>
      </div>
      <MediaPreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </div>
  );
};

export default MediaLibrary;
